/**
 * Which skin is equipped, and getting its art onto the page.
 *
 * Separate from `skin.ts` so that the manifest stays plain data with no React
 * in it: a skin is loaded, validated and reasoned about in places that have no
 * DOM — tests, and eventually the store.
 *
 * ## Where the ids come from
 *
 * The room. The host's equipped deck and table ride on the table section of
 * every packet (D87), so `Room` composes them into a skin and nests a second
 * provider around the table - which is why this takes a skin rather than
 * reading one. The app-wide provider outside it paints every other screen in
 * Classic, and leaving a room takes the host's paint away with it.
 *
 * A client renders ids it does not own. The art is fetched because somebody at
 * this table is wearing it, never because you bought it.
 */

import { createContext, useContext, useMemo } from 'react';
import { CLASSIC, skinVars, validateSkin } from './skin.js';
import { useDeckPrefetch } from './use-deck-prefetch.js';
import type { Skin } from './skin.js';

const SkinContext = createContext<Skin>(CLASSIC);

export function useSkin(): Skin {
  return useContext(SkinContext);
}

/**
 * Puts a skin's art on the page and shares the manifest with anything below.
 *
 * The custom properties go on a real wrapper element rather than on
 * `document.documentElement`, so that the art is scoped to the subtree it
 * belongs to and unmounting takes it away with it. Writing to the root would
 * leave a skin's paint behind after its table closed.
 */
export function SkinProvider({ skin = CLASSIC, children }: { skin?: Skin; children: React.ReactNode }) {
  /*
   * Ask for the rest of the deck while nobody is waiting for it.
   *
   * Here rather than in `Card`, because a card only knows the one it is: the
   * provider is where the whole deck is named, and it is mounted for exactly
   * as long as the deck is in use.
   */
  useDeckPrefetch(skin.faces);

  /*
   * Validated here rather than only in tests.
   *
   * A skin will eventually be JSON fetched at runtime, and a validator nothing
   * calls is a rule nobody enforces. Running it on the way in means the first
   * bought skin already travels a proven path, and a hand-edit to a manifest
   * that breaks H4 says so on the console instead of quietly moving a card.
   *
   * Memoised on the skin so a re-render is not a re-validation, and dropped
   * from production builds: the check is for whoever is authoring a manifest,
   * and by the time a player loads one the answer cannot change.
   */
  const vars = useMemo(() => {
    if (import.meta.env.DEV) {
      const problems = validateSkin(skin);
      if (problems.length > 0) {
        console.error(`skin "${skin.id}" is not valid:\n  ${problems.join('\n  ')}`);
      }
    }
    return skinVars(skin) as React.CSSProperties;
  }, [skin]);

  return (
    <SkinContext.Provider value={skin}>
      <div className="skin-root" style={vars}>
        {children}
      </div>
    </SkinContext.Provider>
  );
}
